import { useCallback, useEffect, useMemo } from "react";
import moment from "moment";
import { ConnectedProps, connect } from "react-redux";
import {
  actions as dashboardActions,
  dashboardSelector,
} from "reducers/dashboard";
import { actions as regionsActions } from "reducers/regions";
import { DashboardFilterType } from "reducers/types/dashboard";
import { AppDispatch, RootState } from "store";
import { DATE_FORMATS } from "utils/constants";
import { DashboardAdmin } from "./DashboardAdmin";
import { DashboardContainerTypes } from "./types";
import { sessionSelector } from "reducers/session";
import useBasicData from "hooks/useBasicData";
import {
  actions as capacityModalActions,
  dashboardSlotCapacitySelector,
} from "reducers/dashboardSlotCapacityModal";

const DashboardContainer = (
  props: DashboardContainerTypes & ConnectorProps
): JSX.Element => {
  const { regions } = useBasicData(["regions"]);
  const transactionUrlFilter = useMemo((): string => {
    const { shortISODate } = DATE_FORMATS;
    const today = moment().format(shortISODate);
    let filters = `minSla=${today}&maxSla=${today}`;
    if (props.filters.regions) {
      filters = `${filters}&regions=${props.filters.regions}`;
    }
    return filters;
  }, [props.filters]);

  useEffect(() => {
    if (regions[0]) {
      const filters = {
        regions: regions.map((r) => r.id).join(","),
      };
      props.setDashboardFilters(filters);
      props.getDashboard(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regions]);

  const refresh = useCallback(() => {
    props.getDashboard(props.filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filters]);

  return (
    <DashboardAdmin
      {...props}
      transactionUrlFilter={transactionUrlFilter}
      regions={regions}
      testId="dashboard"
      refresh={refresh}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  dashboard: dashboardSelector(state).dashboard,
  filters: dashboardSelector(state).filters,
  previousFilters: dashboardSelector(state).previousFilters,
  isSingleRegion: dashboardSelector(state).isSingleRegion,
  showModal: dashboardSlotCapacitySelector(state).showModal,
  countryCode: sessionSelector(state).countryCode,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getDashboard: (params: DashboardFilterType) => {
    dispatch(dashboardActions.getDashboard(params));
  },
  getPointStatus: (params: DashboardFilterType) => {
    dispatch(dashboardActions.getPointStatusRequest(params));
  },
  getDelayedTrips: (params: DashboardFilterType) => {
    dispatch(dashboardActions.getDelayedTripsRequest(params));
  },
  setTripPage: (page: number) => {
    dispatch(dashboardActions.setTripPage(page));
  },
  toggleModal: (show: boolean) => {
    dispatch(capacityModalActions.toggleCapacityDetailModalVisibility(show));
  },
  getWorkzones: (regionId: string) => {
    dispatch(regionsActions.getWorkzonesFetch(regionId));
  },
  setDashboardFilters: (params: DashboardFilterType) => {
    dispatch(dashboardActions.setDashboardFilters(params));
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(DashboardContainer);
